import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl, type IntlShape } from "react-intl";
import { AdminMasterDetail } from "@/components/admin/AdminMasterDetail";
import { Button } from "@/components/design/Button";
import { MenuItem } from "@/components/design/MenuItem";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { TextArea, TextInput } from "@/components/design/TextField";
import {
  EmailTemplateKind,
  EmailTemplateStorageStatus,
  EmailTemplatesDocument,
  UpdateEmailTemplateDocument,
} from "@/graphql/generated/operations";
import { LocalizedError } from "@/lib/localized-error";
import { usePendingScope } from "@/lib/pending-edits";
import { userErrorMessage } from "@/lib/user-error";
import { SettingsCard } from "./SettingsCard";

interface Draft {
  subject: string | null;
  body: string | null;
}

// Validation rules travel with the template: placeholders and length limits come from Genesis, so a limit
// changed there never leaves a stale copy here. Genesis stays the normative validator.
interface ValidatedTemplate extends Draft {
  defaultSubject: string;
  defaultBody: string;
  placeholders: ReadonlyArray<{ token: string; required: boolean }>;
  maxSubjectLength: number;
  maxBodyLength: number;
}

type TemplateView = "editor" | "preview";

type PreviewBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "divider" };

const LINK_TOKEN = "{link}";
const APP_NAME_TOKEN = "{appName}";
const LINK_SENTINEL = "\uE000EMAIL_LINK\uE001";
const TOKEN_PATTERN = /\{[A-Za-z][A-Za-z0-9]*}/g;

function renderPreviewValue(value: string, appName: string): string {
  return value.replaceAll(APP_NAME_TOKEN, () => appName);
}

function previewBlocks(body: string, appName: string): PreviewBlock[] {
  const rendered = body
    .replaceAll(LINK_TOKEN, LINK_SENTINEL)
    .replaceAll(APP_NAME_TOKEN, () => appName)
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n");
  const blocks: PreviewBlock[] = [];
  let paragraph: string[] = [];
  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push({ kind: "paragraph", text: paragraph.join("\n") });
    paragraph = [];
  };

  for (const line of rendered.split("\n")) {
    if (line.trim() === "---") {
      flushParagraph();
      blocks.push({ kind: "divider" });
    } else if (line.trim() === "") {
      flushParagraph();
    } else {
      paragraph.push(line);
    }
  }
  flushParagraph();
  return blocks;
}

function PreviewParagraph({ text, linkLabel }: { text: string; linkLabel: string }) {
  const parts = text.split(LINK_SENTINEL);
  return (
    <p className="whitespace-pre-line text-[13px] leading-6 text-ink/70">
      {parts.map((part, index) => (
        <span key={`${index}-${part}`}>
          {index > 0 ? (
            <span className="font-medium text-ink underline underline-offset-2">{linkLabel}</span>
          ) : null}
          {part}
        </span>
      ))}
    </p>
  );
}

function EmailTemplatePreview({
  subject,
  body,
  appName,
  subjectLabel,
  previewLabel,
  approximationHint,
  linkLabel,
  emptyBodyLabel,
  emptySubjectLabel,
}: {
  subject: string;
  body: string;
  appName: string;
  subjectLabel: string;
  previewLabel: string;
  approximationHint: string;
  linkLabel: string;
  emptyBodyLabel: string;
  emptySubjectLabel: string;
}) {
  const blocks = previewBlocks(body, appName);
  return (
    <div className="box-flat overflow-hidden bg-page" role="region" aria-label={previewLabel}>
      <div className="border-b border-ink/10 px-5 py-4">
        <div className="field-label">{subjectLabel}</div>
        <div className="mt-1 text-sm font-medium text-ink/80">
          {renderPreviewValue(subject, appName) || emptySubjectLabel}
        </div>
      </div>
      <div className="border-b border-ink/10 px-5 py-3">
        <p className="field-meta text-ink/55">{approximationHint}</p>
      </div>
      <div className="mx-auto max-w-[42rem] space-y-4 px-5 py-6">
        {blocks.length === 0 ? (
          <div className="field-meta text-ink/40">{emptyBodyLabel}</div>
        ) : (
          blocks.map((block, index) =>
            block.kind === "divider" ? (
              <hr key={`divider-${index}`} className="border-ink/10" />
            ) : (
              <PreviewParagraph key={`paragraph-${index}`} text={block.text} linkLabel={linkLabel} />
            ),
          )
        )}
      </div>
    </div>
  );
}

function validationMessage(
  intl: IntlShape,
  template: ValidatedTemplate,
  subject: string | null,
  body: string | null,
  appName: string,
): string | null {
  const effectiveSubject = subject ?? template.defaultSubject;
  const effectiveBody = body ?? template.defaultBody;

  if (appName.includes("\r") || appName.includes("\n")) {
    return intl.formatMessage({
      id: "adminSettings.emailTemplates.validation.applicationNameSingleLine",
      defaultMessage: "The app name must contain exactly one line. Change it in system settings.",
    });
  }
  if (effectiveSubject.includes(LINK_TOKEN)) {
    return intl.formatMessage(
      {
        id: "adminSettings.emailTemplates.validation.linkInSubject",
        defaultMessage: "The {token} placeholder is not allowed in the subject.",
      },
      { token: LINK_TOKEN },
    );
  }
  for (const placeholder of template.placeholders) {
    if (placeholder.required && !effectiveBody.includes(placeholder.token)) {
      return intl.formatMessage(
        {
          id: "adminSettings.emailTemplates.validation.requiredPlaceholder",
          defaultMessage: "Content must contain the required {token} placeholder.",
        },
        { token: placeholder.token },
      );
    }
  }
  if (
    effectiveSubject.includes(LINK_SENTINEL) ||
    effectiveBody.includes(LINK_SENTINEL) ||
    appName.includes(LINK_SENTINEL)
  ) {
    return intl.formatMessage({
      id: "adminSettings.emailTemplates.validation.reservedSequence",
      defaultMessage: "The template contains an unsupported control sequence.",
    });
  }

  const allowedTokens = new Set(template.placeholders.map((placeholder) => placeholder.token));
  for (const value of [effectiveSubject, effectiveBody]) {
    for (const token of value.match(TOKEN_PATTERN) ?? []) {
      if (!allowedTokens.has(token)) {
        return intl.formatMessage(
          {
            id: "adminSettings.emailTemplates.validation.unknownPlaceholder",
            defaultMessage: "The placeholder {token} is not supported.",
          },
          { token },
        );
      }
    }
  }

  const renderedSubject = effectiveSubject.replaceAll(APP_NAME_TOKEN, () => appName);
  const renderedBody = effectiveBody.replaceAll(APP_NAME_TOKEN, () => appName);
  if (renderedSubject.includes("\r") || renderedSubject.includes("\n")) {
    return intl.formatMessage({
      id: "adminSettings.emailTemplates.validation.subjectSingleLine",
      defaultMessage: "The subject must contain exactly one line.",
    });
  }
  if (renderedSubject.length > template.maxSubjectLength) {
    return intl.formatMessage(
      {
        id: "adminSettings.emailTemplates.validation.subjectTooLong",
        defaultMessage: "The subject must not exceed {max} characters after inserting the app name.",
      },
      { max: template.maxSubjectLength },
    );
  }
  if (renderedBody.length > template.maxBodyLength) {
    return intl.formatMessage(
      {
        id: "adminSettings.emailTemplates.validation.bodyTooLong",
        defaultMessage: "Content must not exceed {max} characters after inserting the app name.",
      },
      { max: template.maxBodyLength },
    );
  }
  return null;
}

export function EmailTemplatesPanel() {
  const intl = useIntl();
  const { data, loading, error, refetch } = useQuery(EmailTemplatesDocument);
  const [updateEmailTemplate] = useMutation(UpdateEmailTemplateDocument);
  const templates = data?.emailTemplates ?? [];
  const appName = data?.systemSettings?.appName?.trim() || "Smycles";
  const [view, setView] = useState<TemplateView>("editor");
  const [selectedKind, setSelectedKind] = useState<EmailTemplateKind | null>(null);
  const selected = templates.find((template) => template.kind === selectedKind) ?? templates[0];
  const [drafts, setDrafts] = useState<Partial<Record<EmailTemplateKind, Draft>>>({});
  const activeDraft = selected
    ? drafts[selected.kind] ?? { subject: selected.subject ?? null, body: selected.body ?? null }
    : null;
  const draftSubject = activeDraft?.subject ?? null;
  const draftBody = activeDraft?.body ?? null;
  const storedSubject = selected?.subject ?? null;
  const storedBody = selected?.body ?? null;
  // Anything but AVAILABLE is read-only: the store is disabled on this server (rollout default) or not
  // reachable, so a save could not succeed. The fields stay visible so the effective text is still readable.
  const writable = selected?.storageStatus === EmailTemplateStorageStatus.Available;
  // UNAVAILABLE: the stored text is unknown, so the fields stay empty instead of showing a default that may
  // be wrong. DISABLED keeps the registry default, because nothing else can be in effect on that server.
  const unavailable = selected?.storageStatus === EmailTemplateStorageStatus.Unavailable;
  const shownSubject = unavailable ? "" : (draftSubject ?? selected?.defaultSubject ?? "");
  const shownBody = unavailable ? "" : (draftBody ?? selected?.defaultBody ?? "");
  const dirty = Boolean(
    writable && selected && activeDraft && (draftSubject !== storedSubject || draftBody !== storedBody),
  );

  usePendingScope(`email-template:${selected?.kind ?? "none"}`, {
    dirty,
    save: async () => {
      if (!selected || !activeDraft || !writable) return;
      const subject = activeDraft.subject?.trim() || null;
      const body = activeDraft.body?.trim() || null;
      const invalid = validationMessage(intl, selected, subject, body, appName);
      if (invalid) {
        throw new LocalizedError(invalid);
      }
      try {
        await updateEmailTemplate({ variables: { input: { kind: selected.kind, subject, body } } });
        await refetch();
        setDrafts((current) => {
          const next = { ...current };
          delete next[selected.kind];
          return next;
        });
      } catch (cause) {
        throw new LocalizedError(userErrorMessage(intl, cause), { cause });
      }
    },
    discard: () => {
      if (!selected) return;
      setDrafts((current) => {
        const next = { ...current };
        delete next[selected.kind];
        return next;
      });
    },
  });

  const kindLabel = (kind: EmailTemplateKind) =>
    intl.formatMessage({
      id: `adminSettings.emailTemplates.kind.${kind}`,
      defaultMessage: kind === EmailTemplateKind.Invitation ? "Invitation" : kind,
    });

  return (
    <div className="space-y-4">
      {error ? (
        <p className="field-meta text-danger" role="alert">
          {userErrorMessage(intl, error)}
        </p>
      ) : null}
      <AdminMasterDetail
        title={intl.formatMessage({
          id: "adminSettings.emailTemplates.title",
          defaultMessage: "Email templates",
        })}
        count={templates.length}
        list={
          loading && templates.length === 0 ? (
            <div className="field-meta text-ink/55">
              {intl.formatMessage({ id: "adminSettings.emailTemplates.loading", defaultMessage: "Loading…" })}
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {templates.map((template) => (
                <MenuItem
                  key={template.kind}
                  active={template.kind === selected?.kind}
                  onClick={() => setSelectedKind(template.kind)}
                >
                  {kindLabel(template.kind)}
                </MenuItem>
              ))}
            </div>
          )
        }
      >
        {selected && activeDraft ? (
          <SettingsCard
            title={kindLabel(selected.kind)}
            sub={intl.formatMessage({
              id: "adminSettings.emailTemplates.sub",
              defaultMessage: "Subject and content used for the next system email.",
            })}
          >
            <div className="space-y-4">
              {!writable ? (
                <div className="smycles-hint text-[12px] text-ink/70" role="status">
                  {intl.formatMessage(
                    selected.storageStatus === EmailTemplateStorageStatus.Disabled
                      ? {
                          id: "adminSettings.emailTemplates.storageDisabled",
                          defaultMessage:
                            "Editing email templates is not enabled on this server. The template is shown read-only.",
                        }
                      : {
                          id: "adminSettings.emailTemplates.storageUnavailable",
                          defaultMessage:
                            "The template store is currently unreachable. The template is shown read-only and stored changes may not be visible.",
                        },
                  )}
                </div>
              ) : null}
              <div className="flex justify-end">
                <SegmentedControl
                  aria-label={intl.formatMessage({
                    id: "adminSettings.emailTemplates.viewLabel",
                    defaultMessage: "Template view",
                  })}
                  size="sm"
                  value={view}
                  onChange={setView}
                  options={[
                    {
                      value: "editor",
                      label: intl.formatMessage({
                        id: "adminSettings.emailTemplates.viewEditor",
                        defaultMessage: "Editor",
                      }),
                    },
                    {
                      value: "preview",
                      label: intl.formatMessage({
                        id: "adminSettings.emailTemplates.viewPreview",
                        defaultMessage: "Preview",
                      }),
                    },
                  ]}
                />
              </div>

              {view === "editor" ? (
                <>
                  <label className="block">
                    <span className="field-label">
                      {intl.formatMessage({
                        id: "adminSettings.emailTemplates.subject",
                        defaultMessage: "Subject",
                      })}
                    </span>
                    <TextInput
                      value={shownSubject}
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [selected.kind]: { ...activeDraft, subject: event.target.value },
                        }))
                      }
                      disabled={loading || !writable}
                      className="mt-1"
                    />
                  </label>

                  <label className="block">
                    <span className="field-label">
                      {intl.formatMessage({
                        id: "adminSettings.emailTemplates.body",
                        defaultMessage: "Content",
                      })}
                    </span>
                    <TextArea
                      mono
                      value={shownBody}
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [selected.kind]: { ...activeDraft, body: event.target.value },
                        }))
                      }
                      disabled={loading || !writable}
                      rows={18}
                      className="mt-1"
                    />
                  </label>
                </>
              ) : (
                <EmailTemplatePreview
                  subject={shownSubject}
                  body={shownBody}
                  appName={appName}
                  subjectLabel={intl.formatMessage({
                    id: "adminSettings.emailTemplates.subject",
                    defaultMessage: "Subject",
                  })}
                  previewLabel={intl.formatMessage({
                    id: "adminSettings.emailTemplates.previewLabel",
                    defaultMessage: "Email preview",
                  })}
                  approximationHint={intl.formatMessage({
                    id: "adminSettings.emailTemplates.previewApproximationHint",
                    defaultMessage:
                      "This preview is approximate. Keycloak and the recipient's email client determine the final appearance.",
                  })}
                  linkLabel={intl.formatMessage({
                    id: "adminSettings.emailTemplates.previewLink",
                    defaultMessage: "Individual invitation link",
                  })}
                  emptyBodyLabel={intl.formatMessage({
                    id: "adminSettings.emailTemplates.previewEmptyBody",
                    defaultMessage: "No content.",
                  })}
                  emptySubjectLabel={intl.formatMessage({
                    id: "adminSettings.emailTemplates.previewEmptySubject",
                    defaultMessage: "No subject.",
                  })}
                />
              )}

              <p className="field-meta leading-relaxed text-ink/55">
                {intl.formatMessage(
                  {
                    id: "adminSettings.emailTemplates.placeholders",
                    defaultMessage: "Available placeholders: {tokens}",
                  },
                  {
                    tokens: selected.placeholders
                      .map((placeholder) =>
                        placeholder.required
                          ? intl.formatMessage(
                              {
                                id: "adminSettings.emailTemplates.placeholderRequired",
                                defaultMessage: "{token} (required)",
                              },
                              { token: placeholder.token },
                            )
                          : placeholder.token,
                      )
                      .join(", "),
                  },
                )}
              </p>
              <p className="field-meta leading-relaxed text-ink/55">
                {intl.formatMessage({
                  id: "adminSettings.emailTemplates.realmWideHint",
                  defaultMessage:
                    "This text applies to all Keycloak action emails in the realm, including credential resets initiated in the Admin Console.",
                })}
              </p>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="field-meta text-ink/55">
                  {writable && draftSubject === null && draftBody === null
                    ? intl.formatMessage({
                        id: "adminSettings.emailTemplates.defaultActive",
                        defaultMessage: "The default is currently active.",
                      })
                    : null}
                </div>
                {writable && (draftSubject !== null || draftBody !== null) ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setDrafts((current) => ({
                        ...current,
                        [selected.kind]: { subject: null, body: null },
                      }))
                    }
                  >
                    {intl.formatMessage({
                      id: "adminSettings.emailTemplates.reset",
                      defaultMessage: "Use default",
                    })}
                  </Button>
                ) : null}
              </div>
            </div>
          </SettingsCard>
        ) : (
          <SettingsCard
            title={intl.formatMessage({
              id: "adminSettings.emailTemplates.title",
              defaultMessage: "Email templates",
            })}
          >
            <div className="field-meta text-ink/55">
              {intl.formatMessage({
                id: "adminSettings.emailTemplates.empty",
                defaultMessage: "No email templates are available.",
              })}
            </div>
          </SettingsCard>
        )}
      </AdminMasterDetail>
    </div>
  );
}
